import { Fragment, useMemo, useState } from "react";
import { statusLabel, statusColor } from "./constants";
import DateNavBar, { toLocalDate, atNoon, getWeekBounds, groupHeading } from "./DateNavBar";

const torkName = (torks, torkId) => torks.find(t => t.id === torkId)?.name || "–";
const fmt = (d) => d ? new Date(d).toLocaleDateString("sv-SE") : "–";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "–";

const COL_COUNT = 10;

const OrderRow = ({ o, torks, onShowHistory }) => (
    <tr className="border-b hover:bg-gray-50">
        <Td><span className="font-medium">{o.orderId}</span></Td>
        <Td>{o.type || "–"}</Td>
        <Td>{o.package?.dimension || "–"}</Td>
        <Td>{o.package?.moisture || "–"}</Td>
        <Td>{torkName(torks, o.torkId)}</Td>
        <Td>{fmt(o.package?.sawedAt)}</Td>
        <Td>{fmtDateTime(o.dates?.dryFinished)}</Td>
        <Td>
            {o.comment
                ? <span className="text-gray-700 max-w-xs block truncate" title={o.comment}>{o.comment}</span>
                : <span className="text-gray-400">–</span>
            }
        </Td>
        <Td>
            <span className={"px-2 py-0.5 rounded-full text-xs " + statusColor(o.status)}>
                {statusLabel(o.status)}
            </span>
        </Td>
        <Td>
            <button
                onClick={() => onShowHistory(o)}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 text-xs"
            >
                Visa loggar
            </button>
        </Td>
    </tr>
);

/* History table: closed orders filtered by dryFinished date. */
const History = ({ allOrders, torks, onShowHistory }) => {
    const [viewMode, setViewMode] = useState("week");
    const [anchorDate, setAnchorDate] = useState(() => toLocalDate(new Date()));

    const today = toLocalDate(new Date());

    const filtered = useMemo(() => {
        const closed = allOrders
            .filter(o => o.status === "closed" && o.dates?.dryFinished)
            .sort((a, b) => new Date(b.dates.dryFinished) - new Date(a.dates.dryFinished));

        if (viewMode === "day") {
            return closed.filter(o => toLocalDate(new Date(o.dates.dryFinished)) === anchorDate);
        }
        if (viewMode === "week") {
            const { mon, sun } = getWeekBounds(atNoon(anchorDate));
            return closed.filter(o => {
                const d = new Date(o.dates.dryFinished);
                return d >= mon && d <= sun;
            });
        }
        return closed;
    }, [allOrders, viewMode, anchorDate]);

    // Ordered day-groups for week view (most recent day first, matching sort order)
    const dayGroups = [];
    if (viewMode === "week") {
        for (const o of filtered) {
            const date = toLocalDate(new Date(o.dates.dryFinished));
            const group = dayGroups[dayGroups.length - 1];
            if (group && group.date === date) group.orders.push(o);
            else dayGroups.push({ date, orders: [o] });
        }
    }

    return (
        <div>
            <DateNavBar
                viewMode={viewMode}
                anchorDate={anchorDate}
                onViewModeChange={setViewMode}
                onAnchorDateChange={setAnchorDate}
            />

            {filtered.length === 0 ? (
                <p className="text-gray-500 p-4">Inga avslutade torkningar under vald period.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600 text-left">
                            <tr>
                                <Th>Order-ID</Th>
                                <Th>Typ</Th>
                                <Th>Dimension</Th>
                                <Th>Fukt</Th>
                                <Th>Tork</Th>
                                <Th>Sågad</Th>
                                <Th>Klar</Th>
                                <Th>Kommentar</Th>
                                <Th>Status</Th>
                                <Th></Th>
                            </tr>
                        </thead>
                        <tbody>
                            {viewMode === "week"
                                ? dayGroups.map(group => (
                                    <Fragment key={group.date}>
                                        <tr>
                                            <td
                                                colSpan={COL_COUNT}
                                                className={
                                                    "px-3 py-2 text-sm font-semibold border-b " +
                                                    (group.date === today
                                                        ? "bg-blue-50 text-blue-700"
                                                        : "bg-gray-50 text-gray-700")
                                                }
                                            >
                                                {(group.date === today ? "Idag · " : "") + groupHeading(group.date)} ({group.orders.length})
                                            </td>
                                        </tr>
                                        {group.orders.map(o => (
                                            <OrderRow key={o.id} o={o} torks={torks} onShowHistory={onShowHistory} />
                                        ))}
                                    </Fragment>
                                ))
                                : filtered.map(o => (
                                    <OrderRow key={o.id} o={o} torks={torks} onShowHistory={onShowHistory} />
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const Th = ({ children }) => <th className="px-3 py-2 font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2">{children}</td>;

export default History;
